export type SaaSPlan = "guest" | "free" | "pro"

export type UsageSummary = {
  plan: SaaSPlan
  dailyLimit: number
  usedToday: number
  remainingToday: number
  totalReports: number
}

export type SaaSUser = {
  id: string
  email: string
  plan: Exclude<SaaSPlan, "guest">
  createdAt: string
  fullName?: string
  mobile?: string
  isCorporateVerified: boolean
  corporateEmail?: string
}

export const PLAN_LIMITS: Record<SaaSPlan, number> = {
  guest: 4,
  free: 5,
  pro: 500,
}

export const DEFAULT_GUEST_USAGE: UsageSummary = {
  plan: "guest",
  dailyLimit: PLAN_LIMITS.guest,
  usedToday: 0,
  remainingToday: PLAN_LIMITS.guest,
  totalReports: 0,
}

export const AUTH_TOKEN_KEY = "digitup_auth_token"
export const GUEST_TOKEN_KEY = "digitup_guest_token"
export const GUEST_USAGE_KEY = "digitup_guest_usage"
export const GUEST_FINGERPRINT_KEY = "digitup_guest_fingerprint"

export const createGuestToken = () =>
  `guest_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`

export const createGuestFingerprint = () => {
  if (typeof window === "undefined") {
    return "guest-server"
  }

  const timezone =
    Intl.DateTimeFormat().resolvedOptions().timeZone || "unknown-timezone"
  const screenSize =
    typeof window.screen !== "undefined"
      ? `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth || 0}`
      : "unknown-screen"
  const parts = [
    navigator.userAgent || "unknown-ua",
    navigator.language || "unknown-language",
    timezone,
    screenSize,
    navigator.platform || "unknown-platform",
    String(navigator.hardwareConcurrency || 0),
  ]

  return parts.join("|")
}

export const getTodayKey = () => new Date().toISOString().slice(0, 10)

export const readGuestUsage = (toolId = "url-extractor"): UsageSummary => {
  if (typeof window === "undefined") {
    return DEFAULT_GUEST_USAGE
  }

  try {
    const key = `${GUEST_USAGE_KEY}_${toolId}`
    let raw = window.localStorage.getItem(key)
    if (!raw && toolId === "url-extractor") {
      // Backwards compatibility fallback for url-extractor
      raw = window.localStorage.getItem(GUEST_USAGE_KEY)
    }

    if (!raw) {
      return DEFAULT_GUEST_USAGE
    }

    const parsed = JSON.parse(raw)
    if (parsed?.date !== getTodayKey()) {
      return DEFAULT_GUEST_USAGE
    }

    const usedToday = Number(parsed.usedToday) || 0
    const totalReports = Number(parsed.totalReports) || 0

    return {
      plan: "guest",
      dailyLimit: PLAN_LIMITS.guest,
      usedToday,
      remainingToday: Math.max(PLAN_LIMITS.guest - usedToday, 0),
      totalReports,
    }
  } catch {
    return DEFAULT_GUEST_USAGE
  }
}

export const writeGuestUsage = (toolId: string, usage: UsageSummary) => {
  if (typeof window === "undefined") {
    return
  }

  window.localStorage.setItem(
    `${GUEST_USAGE_KEY}_${toolId}`,
    JSON.stringify({
      date: getTodayKey(),
      usedToday: usage.usedToday,
      totalReports: usage.totalReports,
    })
  )
}

const PERSONAL_EMAIL_DOMAINS = new Set([
  "gmail.com",
  "yahoo.com",
  "hotmail.com",
  "outlook.com",
  "icloud.com",
  "aol.com",
  "zoho.com",
  "proton.me",
  "protonmail.com",
  "yandex.com",
  "mail.com",
  "gmx.com",
  "live.com",
  "msn.com",
  "ymail.com",
  "googlemail.com",
  "yahoo.co.uk",
  "yahoo.co.in",
  "rediffmail.com",
  "zoho.in",
])

export const isCorporateEmail = (email: string): boolean => {
  const cleanEmail = (email || "").trim().toLowerCase()
  const parts = cleanEmail.split("@")
  if (parts.length !== 2) {
    return false
  }
  const domain = parts[1]
  return !PERSONAL_EMAIL_DOMAINS.has(domain)
}
